// 計算履歴管理フック

import { useState, useEffect, useCallback } from 'react';
import { CalculationHistory, CalculationInput, CalculationResult } from '../types';
import { 
  getCalculationHistory, 
  addCalculationHistory, 
  clearCalculationHistory, 
  removeCalculationHistory 
} from '../utils/storage';

export function useCalculationHistory() {
  const [history, setHistory] = useState<CalculationHistory[]>([]);

  // 初期化時に履歴を読み込み
  useEffect(() => {
    try {
      const savedHistory = getCalculationHistory();
      setHistory(savedHistory);
    } catch (error) {
      console.error('Failed to load calculation history:', error);
      setHistory([]);
    }
  }, []);

  // 履歴に追加
  const addToHistory = useCallback((
    input: CalculationInput,
    result: CalculationResult
  ) => {
    addCalculationHistory(input, result);
    
    // 状態を更新
    const updatedHistory = getCalculationHistory();
    setHistory(updatedHistory);
  }, []);

  // 履歴をクリア
  const clearHistory = useCallback(() => {
    clearCalculationHistory();
    setHistory([]);
  }, []);

  // 特定の履歴アイテムを削除
  const removeFromHistory = useCallback((id: string) => {
    removeCalculationHistory(id);
    
    // 状態を更新
    const updatedHistory = getCalculationHistory();
    setHistory(updatedHistory);
  }, []);

  // 履歴の統計情報
  const historyStats = useCallback(() => {
    if (history.length === 0) {
      return {
        totalCalculations: 0,
        uniqueMaterials: 0,
        totalPieces: 0,
        mostUsedMaterial: null,
      };
    }

    const totalCalculations = history.length;
    const materialCounts: { [key: string]: number } = {};
    let totalPieces = 0;

    history.forEach(item => {
      const material = item.input.materialName;
      materialCounts[material] = (materialCounts[material] || 0) + 1;
      totalPieces += item.result.totalPieces;
    });

    const uniqueMaterials = Object.keys(materialCounts).length;
    const mostUsedMaterial = Object.entries(materialCounts).reduce(
      (max, [material, count]) => count > max.count ? { material, count } : max,
      { material: '', count: 0 }
    );

    return {
      totalCalculations,
      uniqueMaterials,
      totalPieces,
      mostUsedMaterial: mostUsedMaterial.material || null,
    };
  }, [history]);

  // 最近の履歴を取得（最大n件）
  const getRecentHistory = useCallback((limit: number = 10) => {
    return history.slice(0, limit);
  }, [history]);

  // 特定の部材名で履歴をフィルタ
  const filterByMaterial = useCallback((materialName: string) => {
    return history.filter(item => item.input.materialName === materialName);
  }, [history]);

  // 特定の期間で履歴をフィルタ
  const filterByDateRange = useCallback((startDate: Date, endDate: Date) => {
    return history.filter(item => {
      const itemDate = item.timestamp;
      return itemDate >= startDate && itemDate <= endDate;
    });
  }, [history]);

  // 履歴をCSV用のデータに変換
  const getHistoryForCsv = useCallback(() => {
    return history.map(item => ({
      result: item.result,
      pieceWidth: item.input.pieceWidth,
      pieceHeight: item.input.pieceHeight,
      kerf: item.input.kerf,
    }));
  }, [history]);

  return {
    history,
    addToHistory,
    clearHistory,
    removeFromHistory,
    historyStats,
    getRecentHistory,
    filterByMaterial,
    filterByDateRange,
    getHistoryForCsv,
  };
}